import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent } from '@sentry/react';
import type { TransactionEvent } from '@sentry/core';
import { getSentryUserContext } from './sentryUserContext';
import { GraphqlRequestError } from './graphqlRequestError';
import { LearnosityFetchError, type LearnosityFetchOperation } from '../bffApiCalls/LearnosityFetchError';
import {
  LearnosityItemsSdkError,
  LearnosityInitializationPayloadError,
  LearnosityReportAccessDeniedError,
  LearnosityRetryMismatchError,
  LearnositySdkTimeoutError,
  isLearnosityScriptFailure,
} from './learnosityErrors';
import { LearnosityTransportError } from './learnosityXdomainTransport';

export type LearnosityApi = 'items' | 'reports';
export type LearnosityErrorCode = number | string;
export type LearnosityRenderPhase = 'before_ready' | 'after_ready' | 'unknown';
export type LearnosityLifecyclePhase = 'init' | 'ready' | 'error' | 'reset';

/** The documented fields passed to Learnosity error callbacks. */
export interface LearnosityErrorShape {
  code?: LearnosityErrorCode;
  msg?: string;
  detail?: string;
  version?: string;
  vendorVersion?: string;
}

export interface LearnosityContainerState {
  connected: boolean;
  visible: boolean;
  width: number;
  height: number;
  documentVisibility: 'visible' | 'hidden' | 'prerender' | 'unknown';
}

/**
 * Application-owned context. Callers must construct this explicitly; never
 * spread initialization objects, responses, reports, or callback objects here.
 */
export interface LearnosityTelemetryFields {
  api?: LearnosityApi;
  code?: LearnosityErrorCode;
  msg?: string;
  detail?: string;
  assessmentType?: string;
  activityReference?: string;
  activityTitle?: string;
  renderingType?: string;
  assessmentState?: string;
  appVersion?: string;
  learnosityVersion?: string;
  phase?: LearnosityLifecyclePhase;
  renderPhase?: LearnosityRenderPhase;
  elapsedMs?: number;
  retryAttempt?: number;
  retryDelayMs?: number;
  waitedMs?: number;
  operation?: string;
  fetchOperation?: LearnosityFetchOperation;
  errorCategory?: LearnosityFailureCategory;
  status?: number;
  statusText?: string;
  correlationId?: string;
  graphqlOperation?: string;
  graphqlErrorCodes?: string[];
  graphqlErrorMessages?: string[];
  networkError?: boolean;
  terminalStartup?: boolean;
  startupCycleId?: string;
  container?: LearnosityContainerState;
  learnerId?: string;
  sessionId?: string;
  learnerIds?: string[];
  sessionIds?: string[];
  responseSessionId?: string | null;
  responseStatus?: string;
  responseMessage?: string;
  responseCount?: number;
  previousActivityReference?: string;
  action?: 'check' | 'auto_submit' | 'submit_error';
  question?: number;
  nextCheckAt?: number;
  gate?: 'early' | 'late' | 'universal';
  abilityEstimate?: number | null;
  standardError?: number | null;
  submissionMode?: 'automatic' | 'manual';
}

export type LearnosityLogFields = Omit<LearnosityTelemetryFields, 'phase'> & {
  phase?: 'request' | 'response';
};

export interface LearnosityTelemetry {
  markInit: () => void;
  markReady: () => void;
  captureError: (error: unknown, operation?: string, context?: { terminalStartup: true }) => void;
  markReset: () => void;
}

export interface CreateLearnosityTelemetryOptions {
  startupCycleId?: string;
  assessmentType: string;
  activityReference?: string;
  renderingType?: string;
  assessmentState?: string;
  getContainer: () => HTMLElement | null | undefined;
  appVersion?: string;
  learnerId?: string;
  sessionId?: string;
}

const LEARNOSITY_CATEGORY = 'Learnosity';
const LEARNOSITY_BFF_CATEGORY = 'Learnosity BFF';
const DEFAULT_APP_VERSION = import.meta.env.VITE_RELEASE_VERSION || 'unknown';
const BFF_PREFIX = '/v1/learnosity/';
const ignoredDomMissingAssessmentTypes = new Set(['inline', 'inline_assessment', 'exit_slip_print']);
const lifecycleOperations = new Set<LearnosityLifecyclePhase>(['init', 'ready', 'error', 'reset']);

const bffRoutes = [
  ['/v1/learnosity/assessments/incomplete-status', 'get_incomplete_status'],
  ['/v1/learnosity/assessments/status', 'assessment_status'],
  ['/v1/learnosity/assessments/submit-success', 'submit_assessment'],
  ['/v1/learnosity/diagnostics/ability-estimate', 'diagnostic_ability_estimate'],
  ['/v1/learnosity/diagnostics/submit-success', 'submit_assessment'],
  ['/v1/learnosity/exitslip/submit-success', 'submit_assessment'],
  ['/v1/learnosity/init-object', 'init_object'],
  ['/v1/learnosity/item-references/', 'get_item_references'],
  ['/v1/learnosity/learnosity-report', 'reports'],
] as const;

type CallbackFields = Pick<LearnosityTelemetryFields, 'code' | 'msg' | 'detail' | 'learnosityVersion'> & {
  message?: string;
};

type AdaptedLearnosityError = {
  exception: Error;
  fields: LearnosityTelemetryFields;
};

type HttpBreadcrumbData = {
  url?: string;
  'http.url'?: string;
  'http.query'?: unknown;
  'http.fragment'?: unknown;
  status_code?: number;
  statusCode?: number;
  status?: number;
  [key: string]: unknown;
};

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isLearnosityCode(value: unknown): value is LearnosityErrorCode {
  return (typeof value === 'number' && Number.isFinite(value)) || isNonBlankString(value);
}

function readLearnosityCallback(error: unknown): CallbackFields {
  if (!error || typeof error !== 'object') return {};

  const callback = error as LearnosityErrorShape & { message?: unknown };
  const version = isNonBlankString(callback.vendorVersion)
    ? callback.vendorVersion
    : isNonBlankString(callback.version)
    ? callback.version
    : undefined;

  return {
    ...(isLearnosityCode(callback.code) && { code: callback.code }),
    ...(isNonBlankString(callback.msg) && { msg: callback.msg }),
    ...(isNonBlankString(callback.detail) && { detail: callback.detail }),
    ...(version && { learnosityVersion: version }),
    ...(isNonBlankString(callback.message) && { message: callback.message }),
  };
}

function getBrowserLearnosityVersion(): string | undefined {
  const version = typeof window === 'undefined' ? undefined : window.LearnosityItems?.version;
  return isNonBlankString(version) ? version : undefined;
}

function isNetworkFetchError(error: unknown, callback: CallbackFields): boolean {
  const messages = [
    typeof error === 'string' ? error : undefined,
    error instanceof Error ? error.message : undefined,
    callback.message,
    callback.msg,
    callback.detail,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.toLowerCase());

  return messages.some(
    message =>
      /\bfailed to fetch\b|\bnetwork error\b/.test(message) ||
      message === 'load failed' ||
      message === 'networkerror when attempting to fetch resource.',
  );
}

export type LearnosityFailureCategory =
  | 'timeout'
  | 'transport_handshake'
  | 'sdk_load'
  | 'session_mismatch'
  | 'http'
  | 'invalid_payload'
  | 'access_denied'
  | 'network'
  | 'graphql'
  | 'sdk'
  | 'unknown';

/** Classify the cause that ended a startup. Typed failures take precedence over error shapes. */
export function classifyLearnosityFailure(error: unknown, operation?: string): LearnosityFailureCategory {
  if (operation === 'startup_timeout' || error instanceof LearnositySdkTimeoutError) return 'timeout';
  if (error instanceof LearnosityTransportError) return 'transport_handshake';
  if (error instanceof LearnosityItemsSdkError || isLearnosityScriptFailure(error)) return 'sdk_load';
  if (error instanceof LearnosityRetryMismatchError) return 'session_mismatch';
  if (error instanceof LearnosityReportAccessDeniedError) return 'access_denied';
  if (error instanceof LearnosityInitializationPayloadError) return 'invalid_payload';
  if (error instanceof LearnosityFetchError) return error.category;
  if (error instanceof GraphqlRequestError) return error.diagnostics.category;
  const callback = readLearnosityCallback(error);
  if (isNetworkFetchError(error, callback)) return 'network';
  if (callback.code !== undefined && callback.msg !== undefined) return 'sdk';
  return 'unknown';
}

function fallbackErrorMessage(fields: LearnosityTelemetryFields): string {
  if (fields.operation && fields.status !== undefined) {
    return `Learnosity ${fields.operation} request failed (${fields.status})`;
  }
  if (fields.operation) return `Learnosity ${fields.operation} failed`;
  if (fields.code !== undefined) return `Learnosity error ${String(fields.code)}`;
  return 'Learnosity error';
}

/** Convert the external callback once, retaining only its documented fields. */
function adaptLearnosityError(error: unknown, fields: LearnosityTelemetryFields): AdaptedLearnosityError {
  const callback = readLearnosityCallback(error);
  const requestDiagnostics = error instanceof GraphqlRequestError ? error.diagnostics : undefined;
  const fetchDiagnostics = error instanceof LearnosityFetchError ? error : undefined;
  const networkError =
    fields.networkError ??
    (requestDiagnostics ? requestDiagnostics.category === 'network' : isNetworkFetchError(error, callback));
  const context: LearnosityTelemetryFields = {
    ...fields,
    ...(fetchDiagnostics && {
      status: fetchDiagnostics.status,
      fetchOperation: fetchDiagnostics.operation,
      errorCategory: fetchDiagnostics.category,
    }),
    ...(requestDiagnostics && {
      errorCategory: requestDiagnostics.category,
      status: requestDiagnostics.status,
      ...(requestDiagnostics.category === 'http' && { statusText: requestDiagnostics.statusText }),
      correlationId: requestDiagnostics.correlationId,
      graphqlOperation: requestDiagnostics.operationName,
      ...(requestDiagnostics.category === 'graphql' && {
        graphqlErrorCodes: requestDiagnostics.errorCodes,
        graphqlErrorMessages: requestDiagnostics.errorMessages,
      }),
    }),
    ...(fields.code === undefined && callback.code !== undefined && { code: callback.code }),
    ...(fields.msg === undefined && callback.msg !== undefined && { msg: callback.msg }),
    ...(fields.detail === undefined && callback.detail !== undefined && { detail: callback.detail }),
    appVersion: fields.appVersion ?? DEFAULT_APP_VERSION,
    learnosityVersion:
      callback.learnosityVersion ??
      fields.learnosityVersion ??
      (fields.api === 'items' ? getBrowserLearnosityVersion() : undefined),
    ...(networkError && { networkError: true }),
  };

  if (error instanceof LearnosityReportAccessDeniedError) {
    // An expected relationship denial is filterable even when it ends an SDK recovery cycle.
    context.errorCategory = 'access_denied';
    delete context.terminalStartup;
    delete context.startupCycleId;
  }

  if (error instanceof Error) {
    if (!context.terminalStartup || !context.startupCycleId) return { exception: error, fields: context };
    // Shared loader/signing work can reject several cycles with one Error. Sentry
    // marks captured objects, so each terminal cycle owns a selected copy.
    const exception = new Error(error.message);
    exception.name = error.name;
    exception.stack = error.stack;
    const cause = Object.getOwnPropertyDescriptor(error, 'cause')?.value;
    if (cause instanceof Error) Object.defineProperty(exception, 'cause', { value: cause, configurable: true });
    return { exception, fields: context };
  }
  if (typeof error === 'string') return { exception: new Error(error), fields: context };

  const exception = new Error(fields.msg ?? callback.msg ?? callback.message ?? fallbackErrorMessage(context));
  exception.name = 'LearnosityError';
  return { exception, fields: context };
}

function getActorContext() {
  try {
    const { user, ...tags } = getSentryUserContext();
    return { user, tags };
  } catch {
    return {};
  }
}

function getLearnosityTags(fields: LearnosityTelemetryFields): Record<string, string> {
  return {
    learnosity: 'true',
    ...(isNonBlankString(fields.learnosityVersion) && { learnosity_version: fields.learnosityVersion }),
    ...(fields.api && { learnosity_api: fields.api }),
    ...(fields.code !== undefined && { learnosity_code: String(fields.code) }),
    ...(fields.assessmentType && { learnosity_assessment_type: fields.assessmentType }),
    ...(fields.renderingType && { learnosity_rendering_type: fields.renderingType }),
    ...(fields.assessmentState && { learnosity_assessment_state: fields.assessmentState }),
    ...(fields.renderPhase && { learnosity_render_phase: fields.renderPhase }),
    ...(fields.operation && { learnosity_operation: fields.operation }),
    ...(fields.fetchOperation && { learnosity_fetch_operation: fields.fetchOperation }),
    ...(fields.errorCategory && { learnosity_error_category: fields.errorCategory }),
    ...(fields.status !== undefined && { learnosity_status: String(fields.status) }),
    ...(fields.correlationId && { correlation_id: fields.correlationId }),
    ...(fields.graphqlOperation && { source: 'graphql', graphql_operation: fields.graphqlOperation }),
    ...(fields.graphqlErrorCodes?.length && { graphql_error_codes: fields.graphqlErrorCodes.join(',') }),
    ...(fields.networkError && { learnosity_network_error: 'true' }),
    ...(fields.terminalStartup && { learnosity_init_terminal: 'true' }),
  };
}

function captureAdaptedLearnosityError({ exception, fields }: AdaptedLearnosityError): void {
  const actor = getActorContext();
  Sentry.captureException(exception, {
    ...(actor.user && { user: actor.user }),
    tags: { ...actor.tags, ...getLearnosityTags(fields) },
    contexts: { learnosity: fields as Sentry.Context },
  });
}

function addAdaptedLearnosityErrorBreadcrumb({ fields }: AdaptedLearnosityError): void {
  Sentry.addBreadcrumb({
    category: LEARNOSITY_CATEGORY,
    message: 'Learnosity error',
    level: 'error',
    data: fields,
  });
}

/** Capture either the native Error or one small Learnosity callback adapter. */
export function captureLearnosityException(error: unknown, fields: LearnosityTelemetryFields = {}): void {
  captureAdaptedLearnosityError(adaptLearnosityError(error, fields));
}

export function addLearnosityBreadcrumb(operation: string, fields: LearnosityTelemetryFields = {}): void {
  const context = { ...fields, operation, appVersion: fields.appVersion ?? DEFAULT_APP_VERSION };
  const isLifecycle = lifecycleOperations.has(operation as LearnosityLifecyclePhase);

  Sentry.addBreadcrumb({
    category: LEARNOSITY_CATEGORY,
    message: isLifecycle ? operation : `Learnosity ${operation}`,
    level: context.phase === 'error' ? 'error' : 'info',
    data: context,
  });
}

/** Record an application-owned BFF request without relying on global breadcrumb routing. */
export function addLearnosityBffBreadcrumb(operation: string, fields: LearnosityTelemetryFields = {}): void {
  const context = {
    ...fields,
    operation,
    appVersion: fields.appVersion ?? DEFAULT_APP_VERSION,
    url: bffUrl(operation),
  };

  Sentry.addBreadcrumb({
    category: LEARNOSITY_BFF_CATEGORY,
    message: 'Learnosity BFF request',
    level: context.phase === 'error' ? 'error' : 'info',
    data: context,
  });
}

/** Add selected callback diagnostics without converting a report callback into a new event. */
export function addLearnosityErrorBreadcrumb(error: unknown, fields: LearnosityTelemetryFields = {}): void {
  addAdaptedLearnosityErrorBreadcrumb(
    adaptLearnosityError(error, { ...fields, operation: fields.operation ?? 'error' }),
  );
}

export function logLearnosityInfo(operation: string, fields: LearnosityLogFields = {}): void {
  const { phase, ...context } = fields;
  const actor = getActorContext();
  Sentry.logger.info(`Learnosity ${operation}`, {
    learnosity: true,
    ...context,
    operation,
    appVersion: fields.appVersion ?? DEFAULT_APP_VERSION,
    ...(phase && { phase }),
    ...(actor.user && { actorId: actor.user.id }),
    ...actor.tags,
  });
}

function getPathname(url: string): string {
  try {
    return new URL(url, 'https://learnosity.invalid').pathname;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
}

function bffOperation(pathname: string): string {
  const route = bffRoutes.find(([prefix]) => pathname.startsWith(prefix));
  if (route) return route[1];

  const normalizedOperation = pathname.slice(BFF_PREFIX.length).split('/')[0];
  return bffRoutes.some(([, operation]) => operation === normalizedOperation) ? normalizedOperation : 'bff_request';
}

function bffUrl(operation: string): string {
  return `${BFF_PREFIX}${operation}`;
}

function isLearnosityBffPath(pathname: string | undefined): pathname is string {
  return pathname?.startsWith(BFF_PREFIX) ?? false;
}

function breadcrumbData(breadcrumb: Breadcrumb): HttpBreadcrumbData {
  return (breadcrumb.data ?? {}) as HttpBreadcrumbData;
}

function automaticBffPath(breadcrumb: Breadcrumb): string | undefined {
  const data = breadcrumbData(breadcrumb);
  const url = typeof data.url === 'string' ? data.url : data['http.url'];
  return typeof url === 'string' ? getPathname(url) : undefined;
}

function normalizeAutomaticBffBreadcrumb(breadcrumb: Breadcrumb, pathname: string): Breadcrumb {
  const data = breadcrumbData(breadcrumb);
  const operation = bffOperation(pathname);
  const url = bffUrl(operation);
  const normalized: HttpBreadcrumbData = { ...data, operation };
  if (typeof data.url === 'string') normalized.url = url;
  if (typeof data['http.url'] === 'string') normalized['http.url'] = url;
  delete normalized['http.query'];
  delete normalized['http.fragment'];

  const status = data.status_code ?? data.statusCode ?? data.status;
  if (typeof status === 'number') {
    normalized.status = status;
    delete normalized.status_code;
    delete normalized.statusCode;
  }

  return {
    ...breadcrumb,
    category: LEARNOSITY_BFF_CATEGORY,
    message: 'Learnosity BFF request',
    data: normalized,
  };
}

function containsLearnosityLabel(value: unknown): boolean {
  return typeof value === 'string'
    ? /learnosity/i.test(value)
    : Array.isArray(value) && value.some(entry => typeof entry === 'string' && /learnosity/i.test(entry));
}

function isLearnosityConsoleBreadcrumb(breadcrumb: Breadcrumb, hint?: unknown): boolean {
  if (breadcrumb.category !== 'console') return false;
  const data = breadcrumb.data as { arguments?: unknown } | undefined;
  const input = hint && typeof hint === 'object' ? (hint as { input?: unknown }).input : undefined;
  return (
    containsLearnosityLabel(breadcrumb.message) ||
    containsLearnosityLabel(data?.arguments) ||
    containsLearnosityLabel(input)
  );
}

/** The only global breadcrumb boundary: Learnosity console output and BFF URLs. */
export function sanitizeLearnosityBreadcrumb(breadcrumb: Breadcrumb, hint?: unknown): Breadcrumb | null {
  if (isLearnosityConsoleBreadcrumb(breadcrumb, hint)) {
    return { ...breadcrumb, message: 'Learnosity console breadcrumb', data: { logger: 'console' } };
  }

  if (breadcrumb.category === LEARNOSITY_CATEGORY || breadcrumb.category === LEARNOSITY_BFF_CATEGORY) {
    return breadcrumb;
  }

  const pathname = automaticBffPath(breadcrumb);
  return isLearnosityBffPath(pathname) ? normalizeAutomaticBffBreadcrumb(breadcrumb, pathname) : breadcrumb;
}

/** Normalize only Learnosity BFF spans; unrelated spans are returned by identity. */
export function sanitizeLearnosityTransaction(event: TransactionEvent): TransactionEvent {
  if (!event.spans) return event;

  let changed = false;
  const spans = event.spans.map(span => {
    const data = span.data ?? {};
    const pathname = [data.url, data['http.url']]
      .filter((value): value is string => typeof value === 'string')
      .map(getPathname)
      .find(isLearnosityBffPath);
    if (!pathname) return span;

    const url = bffUrl(bffOperation(pathname));
    const normalized = { ...data };
    if (typeof normalized.url === 'string') normalized.url = url;
    if (typeof normalized['http.url'] === 'string') normalized['http.url'] = url;
    delete normalized['http.query'];
    delete normalized['http.fragment'];
    changed = true;
    return { ...span, data: normalized };
  });

  return changed ? { ...event, spans } : event;
}

export function isExpectedLearnosityDomMissingEvent(event: ErrorEvent): boolean {
  const tags = event.tags ?? {};
  const assessmentType = tags.learnosity_assessment_type ?? tags.assessment_type;
  const eventCode = tags.learnosity_code ?? tags.code;
  return (
    (tags.learnosity === true || tags.learnosity === 'true') &&
    typeof assessmentType === 'string' &&
    ignoredDomMissingAssessmentTypes.has(assessmentType) &&
    String(eventCode) === '50001'
  );
}

export function isLearnosityNetworkErrorEvent(event: ErrorEvent): boolean {
  const tags = event.tags ?? {};
  return (tags.learnosity === true || tags.learnosity === 'true') && tags.learnosity_network_error === 'true';
}

export function getLearnosityContainerState(container: HTMLElement | null | undefined): LearnosityContainerState {
  const connected = Boolean(container?.isConnected);
  let width = 0;
  let height = 0;
  let styleAllowsVisibility = true;

  if (container) {
    const rect = container.getBoundingClientRect?.();
    width = rect?.width || container.clientWidth || 0;
    height = rect?.height || container.clientHeight || 0;
    try {
      if (typeof window !== 'undefined') {
        const style = window.getComputedStyle(container);
        styleAllowsVisibility =
          style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
      }
    } catch {
      styleAllowsVisibility = true;
    }
  }

  const documentVisibility: string = typeof document === 'undefined' ? 'unknown' : document.visibilityState;
  return {
    connected,
    visible: connected && width > 0 && height > 0 && styleAllowsVisibility,
    width: Math.max(0, Math.round(width)),
    height: Math.max(0, Math.round(height)),
    documentVisibility:
      documentVisibility === 'visible' || documentVisibility === 'hidden' || documentVisibility === 'prerender'
        ? documentVisibility
        : 'unknown',
  };
}

export function createLearnosityTelemetry({
  startupCycleId,
  assessmentType,
  activityReference,
  renderingType,
  assessmentState,
  getContainer,
  appVersion,
  learnerId,
  sessionId,
}: CreateLearnosityTelemetryOptions): LearnosityTelemetry {
  let initStartedAt: number | undefined;
  let renderPhase: LearnosityRenderPhase = 'unknown';
  const learnosityVersion = getBrowserLearnosityVersion();

  const baseFields = (): LearnosityTelemetryFields => ({
    api: 'items',
    assessmentType,
    activityReference,
    renderingType,
    assessmentState,
    learnerId,
    sessionId,
    appVersion: appVersion ?? DEFAULT_APP_VERSION,
    learnosityVersion,
    renderPhase,
    ...(initStartedAt !== undefined && { elapsedMs: Date.now() - initStartedAt }),
  });

  return {
    markInit: () => {
      initStartedAt = Date.now();
      renderPhase = 'before_ready';
      addLearnosityBreadcrumb('init', { ...baseFields(), phase: 'init' });
    },
    markReady: () => {
      renderPhase = 'after_ready';
      addLearnosityBreadcrumb('ready', { ...baseFields(), phase: 'ready' });
    },
    captureError: (error, operation, context) => {
      const adapted = adaptLearnosityError(error, {
        ...baseFields(),
        phase: 'error',
        ...(operation && { operation }),
        ...(context && {
          ...(startupCycleId && { startupCycleId }),
          terminalStartup: true,
          renderPhase: 'before_ready',
          errorCategory: classifyLearnosityFailure(error, operation),
        }),
      });
      if (adapted.fields.code === 20017) {
        try {
          adapted.fields.container = getLearnosityContainerState(getContainer());
        } catch {
          // A failed DOM observation must not replace the vendor error.
        }
      }
      addAdaptedLearnosityErrorBreadcrumb(adapted);
      captureAdaptedLearnosityError(adapted);
    },
    markReset: () => {
      addLearnosityBreadcrumb('reset', { ...baseFields(), phase: 'reset' });
      initStartedAt = undefined;
      renderPhase = 'unknown';
    },
  };
}
